import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import type { LibraryVariableSetInfo, ProjectResource, WorkerPoolResource, ActionTemplateParameterResource, LibraryVariableSetResource, EnvironmentResource, GetScopedCommonTemplatesWithValuesBffResponse, ScopedCommonTemplateTenantValue, VariableType, } from "@octopusdeploy/octopus-server-client";
import { VariableSetContentType, Permission } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useState } from "react";
import CommonTemplateValueInlineCell from "~/areas/projects/components/Variables/TenantVariables/CommonTemplateValueInlineCell";
import CommonTemplatesSidebarContent from "~/areas/projects/components/Variables/TenantVariables/CommonTemplatesSidebarContent";
import { CommonTemplatesFiltersBar } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import { MissingValuesCallout } from "~/areas/projects/components/Variables/TenantVariables/MissingValuesCallout";
import NoCommonTemplateValuesOnboarding from "~/areas/projects/components/Variables/TenantVariables/NoCommonTemplateValuesOnboarding";
import NoVariableSetsOnboarding from "~/areas/projects/components/Variables/TenantVariables/NoVariableSetsOnboarding";
import ScopedCommonTemplateScopeInlineCell from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedCommonTemplateScopeInlineCell";
import { extractCommonTemplateValueIds, type ScopedCommonTemplatesScopeChangeTracker, type ScopedCommonTemplatesScopeKey } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedCommonTemplatesScopeChangeTracker";
import { TemplateNameCell } from "~/areas/projects/components/Variables/TenantVariables/TemplateNameCell";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { pagingBarStyles, styles, tenantVariablesDataTable } from "~/areas/projects/components/Variables/TenantVariables/styles";
import { createCommonTemplatesValueKey } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesValueChangeTracker";
import type { CommonTemplatesValueChangeTracker, CommonTemplatesValueKey } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesValueChangeTracker";
import type { TenantVariableFiltersData } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableFiltersData";
import type { TenantVariableResourceOptions } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import { TenantNameCellWithLink } from "~/areas/tenants/components/DataTable/Cells/TenantNameCell";
import NumberedPagingBar from "~/areas/tenants/components/Paging/NumberedPagingBar";
import { repository } from "~/clientInstance";
import { type DoBusyTask, useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import SidebarLayout, { SidebarSide } from "~/components/SidebarLayout/index";
import { generateGuid } from "../../../Process/generation";
interface ScopedCommonTemplatesInlineTabContentProps {
    project: ProjectResource;
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    doBusyTask: DoBusyTask;
    filterState: ProjectTenantVariablesFilterState;
    filterData: TenantVariableFiltersData;
    setFilterState: (filter: ProjectTenantVariablesFilterState) => void;
    pageSize: number;
    setPageState: (newPageNumber: number, newPageSize: number) => void;
    commonTemplatesWithValues: GetScopedCommonTemplatesWithValuesBffResponse;
    onAddTemplateFromOnboarding: (text: string) => void;
    resourceOptions: TenantVariableResourceOptions;
    valueChangeTracker: CommonTemplatesValueChangeTracker;
    scopeChangeTracker: ScopedCommonTemplatesScopeChangeTracker;
}
export function ScopedCommonTemplatesInlineTabContent({ project, connectedEnvironments, doBusyTask, filterState, filterData, setFilterState, pageSize, setPageState, commonTemplatesWithValues, onAddTemplateFromOnboarding, resourceOptions, valueChangeTracker, scopeChangeTracker, }: ScopedCommonTemplatesInlineTabContentProps) {
    const [variableSets, setVariableSets] = useState<LibraryVariableSetResource[] | null>(null);
    useDoBusyTaskEffect(doBusyTask, async () => {
        const libraryVariableSets = await repository.LibraryVariableSets.all({
            ids: project.IncludedLibraryVariableSetIds,
            contentType: VariableSetContentType.Variables,
        });
        setVariableSets(libraryVariableSets);
    }, [project]);
    if (variableSets === null) {
        return null;
    }
    if (variableSets.length === 0) {
        return <NoVariableSetsOnboarding project={project} doBusyTask={doBusyTask} onIncludeVariableSet={onAddTemplateFromOnboarding}/>;
    }
    const missingValueVariableSetIds = new Set<string>(commonTemplatesWithValues.MissingValueLibraryVariableSetIds);
    if (commonTemplatesWithValues.TotalValuesCount === 0) {
        return (<div style={{ height: "100%" }}>
                <SidebarLayout sideBar={<CommonTemplatesSidebarContent variableSets={variableSets} spaceId={project.SpaceId} pageSize={pageSize} filterState={filterState} setFilterState={setFilterState} missingValueVariableSetIds={missingValueVariableSetIds}/>} side={SidebarSide.Left} extendSidebarToEdges={true} extendContentToEdges={true} reduceWidthForSmallResolution={true}>
                    <NoCommonTemplateValuesOnboarding project={project}/>
                </SidebarLayout>
            </div>);
    }
    const [variableValues, bindableTemplateNames] = buildCommonTemplateRowData(commonTemplatesWithValues, variableSets, valueChangeTracker, scopeChangeTracker);
    const unsavedChanges = valueChangeTracker.values().length || scopeChangeTracker.values().length;
    return (<SidebarLayout sideBar={<CommonTemplatesSidebarContent variableSets={variableSets} spaceId={project.SpaceId} pageSize={pageSize} filterState={filterState} setFilterState={setFilterState} missingValueVariableSetIds={missingValueVariableSetIds}/>} side={SidebarSide.Left} extendSidebarToEdges={true} extendContentToEdges={true} reduceWidthForSmallResolution={true}>
            <div>
                {commonTemplatesWithValues.AnyMissingValues && <MissingValuesCallout filterState={filterState} setFilterState={setFilterState}/>}
                <CommonTemplatesFiltersBar totalVariablesCount={commonTemplatesWithValues.TotalValuesCount} filteredVariablesCount={commonTemplatesWithValues.FilteredValuesCount} filterState={filterState} onFilterChange={setFilterState} filterData={filterData} unsavedChangesCount={unsavedChanges}/>
                <ScopedCommonTemplatesDataTable values={variableValues} project={project} accounts={commonTemplatesWithValues.Accounts} certificates={commonTemplatesWithValues.Certificates} workerPools={commonTemplatesWithValues.WorkerPools} connectedEnvironments={connectedEnvironments} resourceOptions={resourceOptions} valueChangeTracker={valueChangeTracker} scopeChangeTracker={scopeChangeTracker} doBusyTask={doBusyTask} bindableTemplateNames={bindableTemplateNames}/>
                <div className={pagingBarStyles}>
                    <NumberedPagingBar totalItems={commonTemplatesWithValues.FilteredValuesCount} pageNumber={filterState.pageNumber} pageSize={pageSize} pageSizeOptions={[30, 50, 100]} onPagingSelectionChange={setPageState}/>
                </div>
            </div>
        </SidebarLayout>);
}
interface ScopedCommonTemplatesDataTableProps {
    accounts: {
        [accountId: string]: string;
    };
    certificates: {
        [certificateId: string]: string;
    };
    workerPools: {
        [workerPoolId: string]: WorkerPoolResource;
    };
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    project: ProjectResource;
    values: CommonTemplateRowData[];
    resourceOptions: TenantVariableResourceOptions;
    valueChangeTracker: CommonTemplatesValueChangeTracker;
    scopeChangeTracker: ScopedCommonTemplatesScopeChangeTracker;
    doBusyTask: DoBusyTask;
    bindableTemplateNames: string[];
}
export function ScopedCommonTemplatesDataTable({ values, project, accounts, certificates, workerPools, connectedEnvironments, resourceOptions, valueChangeTracker, scopeChangeTracker, doBusyTask, bindableTemplateNames, }: ScopedCommonTemplatesDataTableProps) {
    const [focusedValueKey, setFocusedValueKey] = useState<CommonTemplatesValueKey | null>(null);
    const [focusedScopeKey, setFocusedScopeKey] = useState<ScopedCommonTemplatesScopeKey | null>(null);
    const editingPermissions = {
        permission: Permission.VariableEdit,
        project: project.Id,
        wildcard: true,
    };
    const hasEditPermissions = isAllowed(editingPermissions);
    const renderValueCell = (data: CommonTemplateRowData) => {
        const tenantId = data.Variable.TenantId;
        const sourceItems = resourceOptions.get(tenantId);
        const valueKey = data.RowKey;
        const hasValueChanged = valueChangeTracker.has(valueKey);
        const value = hasValueChanged ? valueChangeTracker.get(valueKey) : data.Variable.Value;
        const handleOnBlur = () => {
            setFocusedValueKey((previous: ScopedCommonTemplatesScopeKey | null) => (previous === valueKey ? null : previous));
        };
        return (<CommonTemplateValueInlineCell template={data.Template} accounts={accounts} certificates={certificates} workerPools={workerPools} value={value} valueType={data.VariableType} sourceItems={sourceItems} doBusyTask={doBusyTask} valueTracker={valueChangeTracker} valueKey={valueKey} focused={focusedValueKey === valueKey} onFocus={() => setFocusedValueKey(valueKey)} onBlur={handleOnBlur} bindableTemplateNames={bindableTemplateNames} hasEditPermissions={hasEditPermissions} hasValueChanged={hasValueChanged} initialValue={data.Variable.Value}/>);
    };
    const renderScopeCell = (data: CommonTemplateRowData) => {
        const scopeKey = data.RowKey;
        const hasScopeChanged = scopeChangeTracker.has(scopeKey);
        const handleOnBlur = () => {
            setFocusedScopeKey((previous: CommonTemplatesValueKey | null) => (previous === scopeKey ? null : previous));
        };
        return (<ScopedCommonTemplateScopeInlineCell environments={connectedEnvironments.get(data.Variable.TenantId) || []} scopeTracker={scopeChangeTracker} scopeKey={scopeKey} focused={focusedScopeKey === scopeKey} onFocus={() => setFocusedScopeKey(scopeKey)} onBlur={handleOnBlur} hasEditPermissions={hasEditPermissions} hasScopeChanged={hasScopeChanged} initialEnvironmentScope={data.Scope.EnvironmentIds}/>);
    };
    const groupByVariableSetName = (values: CommonTemplateRowData[]) => values.map((value) => ({
        key: value.VariableSet.Name,
        selector: (data: CommonTemplateRowData) => data.VariableSet.Name,
        header: (<tr style={{ backgroundColor: themeTokens.color.background.secondary.default }}>
                    <td colSpan={5} style={{ padding: `${space[8]} ${space[16]} ${space[8]} ${space[16]}` }}>
                        <div className={styles.variableSetHeader}>{value.VariableSet.Name}</div>
                    </td>
                </tr>),
    }));
    function onAddRow(data: CommonTemplateRowData) {
        const rowKey = createCommonTemplatesValueKey(data.VariableSetId, data.Template.Id, data.Variable.TenantId, generateGuid());
        valueChangeTracker.add(rowKey, undefined);
        scopeChangeTracker.add(rowKey, []);
    }
    function onDeleteRow(values: CommonTemplateRowData[], data: CommonTemplateRowData) {
        // Only remove values and scopes if it is the only template value for a tenant
        const isLastTenantVariable = values.filter((v) => v.Template.Id === data.Template.Id && v.Variable.TenantId === data.Variable.TenantId).length === 1;
        if (isLastTenantVariable) {
            valueChangeTracker.set(data.RowKey, undefined);
            scopeChangeTracker.set(data.RowKey, []);
            return;
        }
        valueChangeTracker.delete(data.RowKey);
        scopeChangeTracker.delete(data.RowKey);
    }
    return (<GroupedDataTable data={values} tableClassName={tenantVariablesDataTable} groupBy={groupByVariableSetName(values)} columns={[
            { title: "Name", render: (data) => (data.IsTemplateChild ? null : <TemplateNameCell template={data.Template}/>), width: "15%", isChild: (data) => data.IsTemplateChild },
            {
                title: "Tenant",
                isChild: (data) => data.IsTenantChild,
                render: (data) => data.IsTenantChild ? null : (<TenantNameCellWithLink spaceId={project.SpaceId} tenantId={data.Variable.TenantId} tenantName={data.Variable.TenantName} tenantLogoLink={data.Variable.TenantLogoLink} isDisabled={data.Variable.TenantIsDisabled} truncate={data.Variable.TenantName.length > 20} linkToPage="Variables"/>),
                width: "20%",
            },
            {
                title: "Value",
                render: renderValueCell,
                width: "15%",
                padding: "0",
                backgroundColor: (data) => {
                    const hasValueChanged = valueChangeTracker.has(data.RowKey);
                    return hasValueChanged ? themeTokens.color.callout.background.attention.default : undefined;
                },
            },
            {
                title: "Scope",
                width: "35%",
                render: renderScopeCell,
                backgroundColor: (data) => {
                    const hasValueChanged = scopeChangeTracker.has(data.RowKey);
                    return hasValueChanged ? themeTokens.color.callout.background.attention.default : undefined;
                },
            },
            {
                title: "",
                render: (data) => {
                    return (<div className={styles.variablesOverflowMenu}>
                                {hasEditPermissions ? (<OverflowMenu accessibleName="variableOverflowMenu" menuItems={[
                                OverflowMenuItems.item("Add", () => {
                                    onAddRow(data);
                                }),
                                OverflowMenuItems.item("Delete", () => {
                                    onDeleteRow(values, data);
                                }),
                            ]}/>) : null}
                            </div>);
                },
                width: "5%",
            },
        ]} getRowKey={(data) => data.RowKey}/>);
}
interface Scope {
    EnvironmentIds: string[];
}
interface CommonTemplateRowData {
    VariableSetId: string;
    VariableSet: LibraryVariableSetInfo;
    Variable: ScopedCommonTemplateTenantValue;
    Template: ActionTemplateParameterResource;
    VariableType: VariableType;
    RowKey: CommonTemplatesValueKey;
    Scope: Scope;
    IsTemplateChild: boolean;
    IsTenantChild: boolean;
}
function buildCommonTemplateRowData(commonTemplatesWithValues: GetScopedCommonTemplatesWithValuesBffResponse, variableSets: LibraryVariableSetResource[], valueChangeTracker: CommonTemplatesValueChangeTracker, scopeChangeTracker: ScopedCommonTemplatesScopeChangeTracker): [
    CommonTemplateRowData[],
    string[]
] {
    const rows: CommonTemplateRowData[] = [];
    const [templatesLookup, bindableNames] = buildTemplatesLookup(variableSets);
    const templatesWithChildren = new Set<string>();
    const removedVariables = valueChangeTracker.removedVariables();
    for (const variableSet of commonTemplatesWithValues.VariableSets) {
        const set = variableSet.LibraryVariableSet;
        for (const templateWithValues of variableSet.Templates) {
            const tenantsWithChildren = new Set<string>();
            const tenantVariablesMap = new Map<string, ScopedCommonTemplateTenantValue[]>();
            for (let i = 0; i < templateWithValues.Values.length; i++) {
                const variable = templateWithValues.Values[i];
                const tenantId = variable.TenantId;
                const existing = tenantVariablesMap.get(tenantId);
                if (existing) {
                    existing.push(variable);
                }
                else {
                    tenantVariablesMap.set(tenantId, []);
                    tenantVariablesMap.get(tenantId)?.push(variable);
                }
            }
            for (const [tenantId, variables] of tenantVariablesMap) {
                const variable = variables[variables.length - 1];
                const newVariablesMap = valueChangeTracker.newVariables(set.Id, variable.TemplateId, variable.TenantId);
                const newScopesMap = scopeChangeTracker.newVariables(set.Id, variable.TemplateId, variable.TenantId);
                for (const [key, newVar] of newVariablesMap) {
                    const { guid } = extractCommonTemplateValueIds(key);
                    const newVariable = {
                        ...variable,
                        Id: guid,
                        Value: newVar,
                        Scope: { EnvironmentIds: newScopesMap.get(key) || [] },
                    };
                    tenantVariablesMap.get(tenantId)?.push(newVariable);
                }
                const variablesToKeep = variables.filter((v) => {
                    const key = createCommonTemplatesValueKey(set.Id, variable.TemplateId, variable.TenantId, v.Id);
                    return !removedVariables.has(key);
                });
                tenantVariablesMap.set(tenantId, variablesToKeep);
            }
            for (const [tenantId, variables] of tenantVariablesMap) {
                for (const variable of variables.values()) {
                    const templateWithType = templatesLookup.get(variable.TemplateId);
                    if (!templateWithType) {
                        logger.warn("Could not find common template with Id {id}", { id: variable.TemplateId });
                        continue;
                    }
                    const { template, type } = templateWithType;
                    const isTemplateHasChildren = templatesWithChildren.has(template.Id);
                    if (!isTemplateHasChildren) {
                        templatesWithChildren.add(template.Id);
                    }
                    const isTenantHasChildren = tenantsWithChildren.has(variable.TenantId);
                    if (!isTenantHasChildren) {
                        tenantsWithChildren.add(variable.TenantId);
                    }
                    const row: CommonTemplateRowData = {
                        VariableSetId: set.Id,
                        VariableSet: set,
                        Variable: variable,
                        Template: template,
                        VariableType: type,
                        RowKey: createCommonTemplatesValueKey(set.Id, template.Id, variable.TenantId, variable.Id),
                        Scope: { EnvironmentIds: variable.Scope.EnvironmentIds },
                        IsTemplateChild: isTemplateHasChildren,
                        IsTenantChild: isTenantHasChildren,
                    };
                    rows.push(row);
                }
            }
        }
    }
    return [rows, bindableNames];
}
interface TemplateWithVariableType {
    template: ActionTemplateParameterResource;
    type: VariableType;
}
function buildTemplatesLookup(variableSets: LibraryVariableSetResource[]): [
    Map<string, TemplateWithVariableType>,
    string[]
] {
    const templates = new Map<string, TemplateWithVariableType>();
    const bindableNames = new Set<string>();
    for (const variableSet of variableSets) {
        for (const template of variableSet.Templates) {
            const type = getVariableTypeFromDisplaySettings(template.DisplaySettings["Octopus.ControlType"]);
            templates.set(template.Id, { template, type });
            bindableNames.add(template.Name);
        }
    }
    return [templates, Array.from(bindableNames)];
}
